"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsx } from 'react/jsx-runtime';
import { isFormula, getFormula } from '@membranehq/sdk';
import { useComboBox } from '../context/use-combobox.mjs';
import { useComboBoxDropdownContext } from '../context/use-combobox-dropdown.mjs';
import { useComboBoxDropdownSearchContext } from '../context/use-combobox-dropdown-search.mjs';
import { Dropdown } from '../Dropdown/Dropdown.mjs';
import { DropdownCategoryType } from '../shared.mjs';
import { ComboboxContainer } from '../../ComboBoxElements/ComboboxContainer/index.mjs';
import { isClickOnEditorCustomTagComponent } from '../../ComboBoxElements/Editor/helpers/clicks.mjs';
import { transformEditorSchemaToValue } from '../../ComboBoxElements/Editor/helpers/transformEditorSchemaToValue.mjs';
import { LexicalEditor } from '../../ComboBoxElements/Editor/LexicalEditor.mjs';
import { useEditor } from '../../ComboBoxElements/Editor/useEditor.mjs';
import { FormulaComponentsContext } from '../../DataBuilder/Formula/use-formula-components.mjs';
import { getRefElement } from '../../../helpers/getRefElement.mjs';
import { useRef, useContext } from 'react';

function ComboboxEditorTrigger({ placeholder, className, onChange, disabled }) {
  const textInputRef = useRef(null);
  const fieldValueRef = useRef(null);
  const {
    value,
    refsToIgnoreClick,
    schema,
    // Dropdown related
    isDropdownOpen,
    openDropdown,
    closeDropdown,
    topDropdownPanelComponent
  } = useComboBox();
  const { setSearchSource, searchSource } = useComboBoxDropdownSearchContext();
  const { pasteTag } = useEditor();
  const { activeCategory } = useComboBoxDropdownContext();
  const formulaComponentMapping = useContext(FormulaComponentsContext);
  function handleOpenDropdown() {
    if (disabled) return;
    openDropdown();
  }
  const refs = [textInputRef, fieldValueRef, ...refsToIgnoreClick ?? []];
  const handleClick = function(e) {
    const isClickInsideAnyOfElements = refs.map(getRefElement).some((el) => el.contains(e?.target));
    if (isClickInsideAnyOfElements && !isClickOnEditorCustomTagComponent(e?.target, fieldValueRef?.current)) {
      handleOpenDropdown();
      e?.stopPropagation();
    }
  };
  const handleOptionSelect = (value2) => {
    if (shouldRewriteValue(activeCategory)) {
      onChange(value2);
      if (shouldCloseDropdown(activeCategory)) {
        closeDropdown();
      }
    } else {
      const openTagCombobox = valueHasDropdownWithFormula(value2, formulaComponentMapping);
      if (openTagCombobox) {
        closeDropdown();
      }
      pasteTag({
        value: value2,
        autoOpen: openTagCombobox,
        allowEditorSearchClear: searchSource === "parent-input"
      });
    }
  };
  return /* @__PURE__ */ jsx(
    ComboboxContainer,
    {
      ref: fieldValueRef,
      "aria-expanded": isDropdownOpen,
      onClick: handleClick,
      disabled,
      level: "editor",
      children: /* @__PURE__ */ jsx(
        Dropdown,
        {
          topDropdownPanelComponent,
          onOptionSelect: handleOptionSelect,
          autoFocusOnSearch: false,
          editorRef: textInputRef,
          children: /* @__PURE__ */ jsx(
            LexicalEditor,
            {
              ref: textInputRef,
              className,
              onFocus: () => {
                if (!value) {
                  setSearchSource("parent-input");
                } else {
                  setSearchSource("indeterminate");
                }
              },
              disabled,
              value,
              schema,
              placeholder,
              onChange: (state) => {
                onChange(transformEditorSchemaToValue(state));
              }
            }
          )
        }
      )
    }
  );
}
function shouldRewriteValue(category) {
  return category && [
    DropdownCategoryType.OPTIONS,
    DropdownCategoryType.CUSTOM,
    DropdownCategoryType.LOOKUP_OPTIONS,
    DropdownCategoryType.SELECT_FROM_UDM_DYNAMICALLY
  ].includes(category);
}
function shouldCloseDropdown(category) {
  return category && [DropdownCategoryType.OPTIONS, DropdownCategoryType.CUSTOM, DropdownCategoryType.LOOKUP_OPTIONS].includes(category);
}
function valueHasDropdownWithFormula(value, mapping) {
  if (!mapping || !isFormula(value)) return false;
  const type = getFormula(value)?.type;
  if (!type) return false;
  return !!mapping[type]?.DropdownComponent;
}

export { ComboboxEditorTrigger };
